/**
 * API client del Portal del Cliente.
 * Session token en localStorage.
 */

const BASE = import.meta.env.VITE_API_URL || "";

// v8.6.22 · Fase 20 · moodboard de coberturas — sus image_url/thumb_url son
// paths relativos de proxy (/api/moodboards/items/:id/image), no absolutos
// como foto_url. Mismo helper que ya usa el OS UI (api/client.js#apiUrl).
export function apiUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith("/api/")) return `${BASE}${path}`;
  return path;
}

function getToken() { return localStorage.getItem("portal_session"); }
export function setToken(t) {
  if (t) localStorage.setItem("portal_session", t);
  else localStorage.removeItem("portal_session");
}
export function setClienteId(id) {
  if (id) localStorage.setItem("portal_cliente_id", id);
  else localStorage.removeItem("portal_cliente_id");
}
export function getClienteId() { return localStorage.getItem("portal_cliente_id"); }

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/api/portal${path}`, {
    method, headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // Limpiar sesión expirada SIN forzar reload (evita loop si AuthContext llama /me sin token)
    if (token) { setToken(null); setClienteId(null); }
    const err = new Error("Sesión expirada o sin permisos");
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    let err;
    try { err = await res.json(); } catch { err = { error: res.statusText }; }
    // Cierre del portal (PORTAL_CERRADO): pantalla «Estamos mejorando tu portal», no un error.
    if (res.status === 503 && err?.codigo === "mantenimiento") {
      try { window.dispatchEvent(new CustomEvent("portal-mantenimiento", { detail: { vuelve: err.vuelve } })); } catch { /* sin window */ }
    }
    const e = new Error(err.error || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export const api = {
  planDelMes: (clienteId) => request("GET", `/cliente/${clienteId}/fee`),
  // Auth · magic link (legacy, mantenido para invitaciones)
  requestLink: (email)          => request("POST", "/auth/request-link", { email }),
  verifyToken: (token)          => request("GET",  `/auth/verify?token=${encodeURIComponent(token)}`),
  // Auth · email + password (Sprint P0)
  signup:        (data)         => request("POST", "/auth/signup", data),
  signin:        (data)         => request("POST", "/auth/signin", data),
  googleSignin:  (idToken)      => request("POST", "/auth/google", { idToken }),
  verifyEmail:   (token)        => request("GET",  `/auth/verify-email?token=${encodeURIComponent(token)}`),
  reenviarVerif: (email)        => request("POST", "/auth/reenviar-verificacion", { email }),
  forgotPassword:(email)        => request("POST", "/auth/forgot-password", { email }),
  resetPassword: (data)         => request("POST", "/auth/reset-password", data),
  changePassword:(data)         => request("POST", "/auth/change-password", data),
  // ---
  logout:      ()               => request("POST", "/auth/logout"),
  me:          ()               => request("GET",  "/me"),
  // Dashboard / Tareas
  dashboard:   (clienteId)      => request("GET",  `/cliente/${clienteId}/dashboard`),
  tareas:      (clienteId, params={}) => request("GET", `/cliente/${clienteId}/tareas?${new URLSearchParams(params)}`),
  tarea:       (tareaId)        => request("GET",  `/tareas/${tareaId}`),
  tareaPreview:(tareaId)        => request("GET",  `/tareas/${tareaId}/preview-files`),
  fortalezasIa:(tareaId)        => request("GET",  `/tareas/${tareaId}/fortalezas-ia`),
  subtareasDeTarea: (tareaId)   => request("GET",  `/tareas/${tareaId}/subtareas`),
  aprobar:     (tareaId, data)  => request("POST", `/tareas/${tareaId}/aprobar`, data),
  pedirCambios:(tareaId, data)  => request("POST", `/tareas/${tareaId}/pedir-cambios`, data),
  cancelarTarea:(tareaId, data) => request("POST", `/tareas/${tareaId}/cancelar`, data),
  reabrirTarea: (tareaId, data) => request("POST", `/tareas/${tareaId}/reabrir`, data),
  // Comments
  comentariosLista: (tareaId)        => request("GET",  `/tareas/${tareaId}/comentarios`),
  comentar:    (tareaId, contenido, attachments)  => request("POST", `/tareas/${tareaId}/comentarios`, { contenido, attachments }),
  // v8.3.8 · Campañas (grupos del OS) + comentarios de documentos
  campanas:              (clienteId)               => request("GET", `/cliente/${clienteId}/campanas`),
  campanaDetalle:        (clienteId, grupoId)      => request("GET", `/cliente/${clienteId}/campanas/${grupoId}`),
  docComentariosLista:   (docId)                   => request("GET", `/documentos/${docId}/comentarios`),
  docComentar:           (docId, contenido, attachments) => request("POST", `/documentos/${docId}/comentarios`, { contenido, attachments }),
  // v8.3.8 · Upload adjunto para comentarios de tarea (base64 → Drive subcarpeta 📎 Comentarios).
  comentarUploadAdjunto: (tareaId, { file_base64, filename, mime_type }) =>
    request("POST", `/tareas/${tareaId}/comentarios/upload`, { file_base64, filename, mime_type }),
  // Pedidos
  pedidos:     (clienteId)      => request("GET",  `/cliente/${clienteId}/pedidos`),
  pedidoCrear: (clienteId, data)=> request("POST", `/cliente/${clienteId}/pedidos`, data),
  // v7.6.3 · IA pedidos
  pedidoSugerir:   (clienteId, texto) => request("POST", `/cliente/${clienteId}/pedidos/sugerir`, { texto }),
  pedidoSimilares: (clienteId, texto) => request("POST", `/cliente/${clienteId}/pedidos/similares`, { texto }),
  pedidosFormatos: ()                 => request("GET",  `/pedidos-formatos`),  // v7.9.0 Atajos Premium
  pedidoETA:       (clienteId, params) => request("GET", `/cliente/${clienteId}/pedidos/eta?${new URLSearchParams(params)}`),
  pedidoDetalle:   (clienteId, pedidoId) => request("GET", `/cliente/${clienteId}/pedidos/${pedidoId}/detalle`),
  // v7.6.4 P4 · Colaboración
  pedidoComentariosListar: (clienteId, pedidoId) => request("GET", `/cliente/${clienteId}/pedidos/${pedidoId}/comentarios`),
  pedidoComentar:          (clienteId, pedidoId, texto, adjuntos) => request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/comentarios`, { texto, adjuntos }),
  pedidoWatchersListar:    (clienteId, pedidoId) => request("GET", `/cliente/${clienteId}/pedidos/${pedidoId}/watchers`),
  pedidoWatcherAgregar:    (clienteId, pedidoId, email) => request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/watchers`, { email }),
  pedidoWatcherRemover:    (clienteId, pedidoId, email) => request("DELETE", `/cliente/${clienteId}/pedidos/${pedidoId}/watchers/${encodeURIComponent(email)}`),
  pedidoAprobarRapido:     (clienteId, pedidoId) => request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/aprobar-rapido`),
  // v7.6.5 P5.3 · Templates propios
  pedidoTemplatesListar:   (clienteId) => request("GET", `/cliente/${clienteId}/pedido-templates`),
  pedidoTemplateCrear:     (clienteId, data) => request("POST", `/cliente/${clienteId}/pedido-templates`, data),
  pedidoTemplateBorrar:    (clienteId, tplId) => request("DELETE", `/cliente/${clienteId}/pedido-templates/${tplId}`),
  pedidoTemplateUsar:      (clienteId, tplId) => request("POST", `/cliente/${clienteId}/pedido-templates/${tplId}/use`),
  // v7.7.0 · Sprint final
  pedidoRecurrenciasListar: (clienteId) => request("GET", `/cliente/${clienteId}/pedido-recurrencias`),
  pedidoRecurrenciaCrear:   (clienteId, data) => request("POST", `/cliente/${clienteId}/pedido-recurrencias`, data),
  pedidoRecurrenciaToggle:  (clienteId, id, activo) => request("PATCH", `/cliente/${clienteId}/pedido-recurrencias/${id}`, { activo }),
  pedidoRecurrenciaBorrar:  (clienteId, id) => request("DELETE", `/cliente/${clienteId}/pedido-recurrencias/${id}`),
  pedidoReaccion:           (clienteId, pedidoId, emoji, target = "pedido", comentario_id = null) =>
    request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/reaccion`, { emoji, target, comentario_id }),
  pedidoAnalizarImagen:     (clienteId, image_base64, mime_type, contexto = "") =>
    request("POST", `/cliente/${clienteId}/pedidos/analizar-imagen`, { image_base64, mime_type, contexto }),
  pedidoArchivar:           (clienteId, pedidoId) => request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/archivar`),
  pedidoDuplicar:           (clienteId, pedidoId) => request("POST", `/cliente/${clienteId}/pedidos/${pedidoId}/duplicar`),
  pedidoEditar:  (id, data)      => request("PATCH",  `/pedidos/${id}`, data),
  pedidoCancelar:(id)            => request("DELETE", `/pedidos/${id}`),
  pedidoReTranscribir:(id)       => request("POST",   `/pedidos/${id}/re-transcribir`, {}),
  // Pedidos AGENCIA → CLIENTE (lo que la agencia le pide al cliente)
  pedidosAgencia:    (clienteId, estado) =>
    request("GET",  `/cliente/${clienteId}/pedidos-agencia${estado ? `?estado=${estado}` : ""}`),
  pedidoAgenciaResponder: (clienteId, pedidoId, data) =>
    request("POST", `/cliente/${clienteId}/pedidos-agencia/${pedidoId}/responder`, data),
  // Planes Mensuales v8.1.0
  planesList:      (clienteId, params={}) => request("GET",  `/cliente/${clienteId}/planes?${new URLSearchParams(params)}`),
  planDetalle:     (clienteId, grupoId)   => request("GET",  `/cliente/${clienteId}/planes/${grupoId}`),
  planAprobarPreliminar: (clienteId, grupoId) => request("POST", `/cliente/${clienteId}/planes/${grupoId}/aprobar-preliminar`, {}),
  planAprobarTodo: (clienteId, grupoId)   => request("POST", `/cliente/${clienteId}/planes/${grupoId}/aprobar-todo`, {}),

  // Calendario / Galería / Reportes (Sprint B+C)
  calendario:  (clienteId, mes) => request("GET",  `/cliente/${clienteId}/calendario${mes ? `?mes=${mes}` : ""}`),
  galeria:     (clienteId, params={}) => request("GET", `/cliente/${clienteId}/galeria?${new URLSearchParams(params)}`),
  // Feed Preview v6.8
  feedPreview:        (clienteId, params={}) => request("GET", `/cliente/${clienteId}/feed?${new URLSearchParams(params)}`),
  feedVersiones:      (clienteId, params={}) => request("GET", `/cliente/${clienteId}/feed/versions?${new URLSearchParams(params)}`),
  feedComentar:       (versionId, data)      => request("POST", `/feed/version/${versionId}/comment`, data),
  feedAprobar:        (versionId, data={})   => request("POST", `/feed/version/${versionId}/approve`, data),
  feedComentarios:    (versionId)            => request("GET",  `/feed/version/${versionId}/comentarios`),
  reportes:    (clienteId)      => request("GET",  `/cliente/${clienteId}/reportes`),
  reporte:     (clienteId, periodo) => request("GET", `/cliente/${clienteId}/reportes/${periodo}`),
  resumenMes:  (clienteId)      => request("GET",  `/cliente/${clienteId}/resumen-mes`),
  // Sprint D
  facturas:    (clienteId)      => request("GET",  `/cliente/${clienteId}/facturas`),
  equipoCliente:(clienteId)     => request("GET",  `/cliente/${clienteId}/equipo`),
  brand:       (clienteId)      => request("GET",  `/cliente/${clienteId}/brand`),
  brandUpdate: (clienteId, data)=> request("PATCH",`/cliente/${clienteId}/brand`, data),
  npsPendiente:(clienteId)      => request("GET",  `/cliente/${clienteId}/nps/pendiente`),
  npsResponder:(clienteId, data)=> request("POST", `/cliente/${clienteId}/nps/responder`, data),
  // Sprint F - PDF reportes + push
  reportePdf:  (clienteId, periodo) => request("GET", `/cliente/${clienteId}/reportes/${periodo}/pdf`),
  pushPublicKey: ()             => request("GET", `/push/public-key`),
  pushSubscribe: (subscription) => request("POST", `/push/subscribe`, { subscription }),
  pushUnsubscribe: (endpoint)   => request("POST", `/push/unsubscribe`, { endpoint }),
  pushTest:    ()               => request("POST", `/push/test`),
  // Notificaciones in-app (Sprint P1A)
  notifs:      (params={})      => request("GET",  `/notifs?${new URLSearchParams(params)}`),
  notifLeida:  (id)             => request("PATCH",`/notifs/${id}/leida`, {}),
  notifMarkAll:()               => request("POST", `/notifs/mark-all-read`, {}),
  notifEliminar:(id)            => request("DELETE",`/notifs/${id}`),
  // Aprobación masiva
  aprobarMasivo:(tareaIds)      => request("POST", `/tareas/aprobar-masivo`, { tarea_ids: tareaIds }),
  // Read state
  marcarVisto:  (entidad, id)   => request("POST", `/read/${entidad}/${id}`, {}),
  vistosBatch:  (entidad, ids)  => request("GET",  `/read/${entidad}/batch?ids=${ids.join(",")}`),
  // Documentos compartidos (Google native)
  documentos:  (clienteId)      => request("GET",  `/cliente/${clienteId}/documentos`),
  documentoVisto: (documentoId) => request("POST", `/documentos/${documentoId}/visto`),
  // Google Calendar nativo (Sprint Google-first)
  calendarEventos:(clienteId, params={}) => request("GET", `/cliente/${clienteId}/calendar/eventos?${new URLSearchParams(params)}`),
  calendarEmbedUrl:(clienteId)  => request("GET", `/cliente/${clienteId}/calendar/embed-url`),
  reunionAgendar: (clienteId, data) => request("POST", `/cliente/${clienteId}/reuniones/agendar`, data),
  blackoutCrear: (clienteId, data) => request("POST", `/cliente/${clienteId}/blackout-dates`, data),
  eventoEliminar:(clienteId, eventId) => request("DELETE", `/cliente/${clienteId}/calendar/eventos/${eventId}`),
  // Cmd+K Search global (Sprint P1B)
  searchGlobal: (clienteId, q)  => request("GET", `/cliente/${clienteId}/search?q=${encodeURIComponent(q)}`),
  // Onboarding tour
  onboardingStatus: ()          => request("GET", `/onboarding/status`),
  onboardingComplete:()         => request("POST", `/onboarding/completar`, {}),
  bienvenidaStatus:  ()         => request("GET",  `/bienvenida/status`),
  bienvenidaVisto:   (modo, paso) => request("POST", `/bienvenida/visto`, { modo, paso }),
  // Sprint P2
  versiones:    (tareaId)       => request("GET", `/tareas/${tareaId}/versiones`),
  reaccionar:   (commentId, emoji) => request("POST", `/comentarios/${commentId}/reaccion`, { emoji }),
  reply:        (commentId, contenido, mentions) => request("POST", `/comentarios/${commentId}/reply`, { contenido, mentions }),
  shareCrear:   (tareaId, data) => request("POST", `/tareas/${tareaId}/share-link`, data),
  shareListar:  (tareaId)       => request("GET", `/tareas/${tareaId}/share-links`),
  shareRevocar: (token)         => request("DELETE", `/share-links/${token}`),
  // v8.5.17 · Reporte mensual como link vivo (mismo mecanismo TTL + max_views)
  reporteShareCrear:  (clienteId, periodo, data) => request("POST", `/cliente/${clienteId}/reportes/${periodo}/share-link`, data),
  reporteShareListar: (clienteId, periodo)       => request("GET", `/cliente/${clienteId}/reportes/${periodo}/share-links`),
  // Sprint P3
  health:        (clienteId)    => request("GET", `/cliente/${clienteId}/health`),
  achievements:  (clienteId)    => request("GET", `/cliente/${clienteId}/achievements`),
  achievementVisto:(id)         => request("POST", `/achievements/${id}/visto`, {}),
  utilization:   (clienteId)    => request("GET", `/cliente/${clienteId}/utilization`),
  // Sprint P5
  favoritosListar: (params={})  => request("GET", `/favoritos?${new URLSearchParams(params)}`),
  favoritoMarcar:  (data)       => request("POST", `/favoritos`, data),
  favoritoDesmarcar:(entidad, id) => request("DELETE", `/favoritos/${entidad}/${id}`),
  moodPendiente:   (clienteId)  => request("GET", `/cliente/${clienteId}/mood/pendiente`),
  moodRegistrar:   (clienteId, data) => request("POST", `/cliente/${clienteId}/mood`, data),
  moodHistorial:   (clienteId, limit=12) => request("GET", `/cliente/${clienteId}/mood/historial?limit=${limit}`),
  exportUrl:       (clienteId, tipo) => `${BASE}/api/portal/cliente/${clienteId}/export/${tipo}`,
  festejosSync:    (clienteId)  => request("POST", `/cliente/${clienteId}/festejos/sync`, {}),
  asistenteChat:   (clienteId, data) => request("POST", `/cliente/${clienteId}/asistente`, data),
  asistenteTranscribir: (clienteId, data) => request("POST", `/cliente/${clienteId}/asistente/transcribir`, data),
  // v7.6.1 S3.1 · Coberturas
  coberturas:       (clienteId, params={}) => request("GET", `/cliente/${clienteId}/coberturas?${new URLSearchParams(params)}`),
  cobertura:        (clienteId, cobId)     => request("GET", `/cliente/${clienteId}/coberturas/${cobId}`),
  // Presentaciones que la agencia compartió (se ven dentro del Portal)
  presentaciones:   (clienteId)            => request("GET", `/cliente/${clienteId}/presentaciones`),
  presentacion:     (clienteId, planId)    => request("GET", `/cliente/${clienteId}/presentaciones/${planId}`),
  // v7.6.1 S3.2 · Chat cliente↔EC
  chatListar:       (clienteId, params={}) => request("GET", `/cliente/${clienteId}/chat?${new URLSearchParams(params)}`),
  chatEnviar:       (clienteId, texto, adjuntos, parent_mensaje_id) => request("POST", `/cliente/${clienteId}/chat`, { texto, adjuntos, parent_mensaje_id }),
  chatLeido:        (clienteId)            => request("POST", `/cliente/${clienteId}/chat/leido`, {}),
  // v7.6.2 C1 · adjuntos + edit/delete + typing
  chatEditar:       (clienteId, msgId, texto)   => request("PATCH", `/cliente/${clienteId}/chat/${msgId}`, { texto }),
  chatBorrar:       (clienteId, msgId)          => request("DELETE", `/cliente/${clienteId}/chat/${msgId}`),
  chatTyping:       (clienteId)                 => request("POST", `/cliente/${clienteId}/chat/typing`, {}),
  chatUpload:       (clienteId, data)           => request("POST", `/cliente/${clienteId}/chat/upload`, data),
  // v7.6.2 C2 · threads + reacciones + convertir
  chatThread:       (clienteId, msgId)          => request("GET",  `/cliente/${clienteId}/chat/${msgId}/thread`),
  chatReaccion:     (clienteId, msgId, emoji)   => request("POST", `/cliente/${clienteId}/chat/${msgId}/reaccion`, { emoji }),
  chatConvertir:    (clienteId, msgId, tipo, params) => request("POST", `/cliente/${clienteId}/chat/${msgId}/convertir`, { tipo, params }),
  // v7.6.2 C3 · read receipts (leidos via chatLeido) · presencia · búsqueda · pin
  chatPresencia:    (clienteId)                 => request("GET",  `/cliente/${clienteId}/chat/presencia`),
  chatBuscar:       (clienteId, params={})      => request("GET",  `/cliente/${clienteId}/chat/search?${new URLSearchParams(params)}`),
  chatPinnedList:   (clienteId)                 => request("GET",  `/cliente/${clienteId}/chat/pinned`),
  chatPin:          (clienteId, msgId)          => request("POST", `/cliente/${clienteId}/chat/${msgId}/pin`, {}),
  chatListarMas:    (clienteId, before, limit=50) => request("GET", `/cliente/${clienteId}/chat?${new URLSearchParams({ before, limit })}`),
  // v7.6.2 C4 · IA
  chatSuggestions:  (clienteId)                 => request("GET",  `/cliente/${clienteId}/chat/suggestions`),
  chatResumir:      (clienteId)                 => request("POST", `/cliente/${clienteId}/chat/resumir`, {}),
  // v7.6.1 P2.8 · Sesiones activas
  sesionesListar:   ()                     => request("GET",  `/sesiones`),
  sesionCerrar:     (idMasked)             => request("DELETE", `/sesiones/${encodeURIComponent(idMasked)}`),
  sesionesCerrarOtras: ()                  => request("POST", `/sesiones/cerrar-otras`, {}),
};

// Para descargas (CSV) hace falta token en query o header. Helper:
export async function downloadCsv(clienteId, tipo) {
  const token = getToken();
  const res = await fetch(`${BASE}/api/portal/cliente/${clienteId}/export/${tipo}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("No se pudo descargar");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const fechaHoy = new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  a.download = `${tipo}-${fechaHoy}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}
